import { createNode } from '@glueapp/graph-types';

export const FilterMapList = createNode({
    id: 'Filter Map List',
    name: 'Filter Map List',
    description:
        'Filter and transform items from a list in one operation. Uses a function to determine what to keep and transform the kept items.',
    aiDescription: [
        'Filters and transforms a list in one pass using a processor function. Async — uses executeFunctionNode.',
        'Setup steps:',
        '1. Create a function with inputs "Item" (type T), "Index" (number), "List" (array of T) and outputs "Keep" (boolean), "New Item" (type R).',
        '   Inside the function, output Keep=true to keep the item and "New Item" as the transformed value.',
        '2. Add this node: glue add "Filter Map List"',
        '3. Connect a list to "Input List".',
        '4. Connect the function node\'s output to the "Processor" port (function type).',
        'Outputs: "Result" (array of R, only kept items after transformation), "Kept Count" (number), "Filtered Count" (number).',
    ].join('\n'),
    category: 'List',
    color: '#778ed5',
    isAsync: true,

    inPorts: {
        'Input List': {
            kind: 'array',
            subType: {
                kind: 'generic',
                identifier: 'T',
            },
        },
        Processor: {
            kind: 'function',
            inputTypes: {
                Item: { kind: 'generic', identifier: 'T' },
                Index: { kind: 'number' },
                List: {
                    kind: 'array',
                    subType: { kind: 'generic', identifier: 'T' },
                },
            },
            outputTypes: {
                Keep: { kind: 'boolean' },
                'New Item': { kind: 'generic', identifier: 'R' },
            },
        },
    },

    outPorts: {
        Result: {
            kind: 'array',
            subType: {
                kind: 'generic',
                identifier: 'R',
            },
        },
        'Kept Count': {
            kind: 'number',
        },
        'Filtered Count': {
            kind: 'number',
        },
    },

    execute: async ({ inPorts, executeFunctionNode }) => {
        if (!executeFunctionNode) {
            throw new Error('Function execution not available');
        }

        const list = inPorts['Input List'] as any[];
        const processorValue = inPorts['Processor'];

        // Check if the processor is a FunctionReferenceValue
        if (
            !processorValue ||
            typeof processorValue !== 'object' ||
            !('isFunctionReferenceValue' in processorValue) ||
            !processorValue.isFunctionReferenceValue
        ) {
            throw new Error('Processor must be a function reference');
        }

        const processor = processorValue as any; // Type as FunctionReferenceValue

        if (!list || !Array.isArray(list)) {
            return {
                output: {
                    Result: [],
                    'Kept Count': 0,
                    'Filtered Count': 0,
                },
            };
        }

        const resultItems = [];

        for (let i = 0; i < list.length; i++) {
            const item = list[i];

            // Execute the processor function using the function ID from the reference
            const result = await executeFunctionNode(processor.functionId, {
                Item: item,
                Index: i,
                List: list,
            });

            const shouldKeep = Boolean(result.output.Keep);

            if (shouldKeep) {
                const transformedItem = result.output['New Item'];
                resultItems.push(transformedItem);
            }
        }

        return {
            output: {
                Result: resultItems,
                'Kept Count': resultItems.length,
                'Filtered Count': list.length - resultItems.length,
            },
        };
    },
});
